import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Check, Users, X } from 'lucide-react';
import React, { useState, useRef, useEffect } from 'react';

const dropdownVariants = {
  hidden: {
    opacity: 0,
    y: -10,
    scale: 0.95
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 25
    }
  },
  exit: {
    opacity: 0,
    y: -10,
    scale: 0.95,
    transition: {
      duration: 0.2
    }
  }
};

const EmployeeSelector = ({
  employees = [],
  selectedEmployee,
  onSelect,
  placeholder = "Select employee",
  multiple = false,
  className = "",
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  const filteredEmployees = employees.filter(employee =>
    employee.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    employee.position.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const selectedEmployeeData = selectedEmployee
    ? employees.find(emp => emp.id === selectedEmployee)
    : null;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (employee) => {
    if (multiple) {
      // Handle multiple selection
      const currentSelection = Array.isArray(selectedEmployee) ? selectedEmployee : [];
      const isSelected = currentSelection.includes(employee.id);

      if (isSelected) {
        onSelect(currentSelection.filter(id => id !== employee.id));
      } else {
        onSelect([...currentSelection, employee.id]);
      }
    } else {
      onSelect(employee.id);
      setIsOpen(false);
      setSearchQuery('');
    }
  };

  const handleClear = () => {
    onSelect(multiple ? [] : null);
    setSearchQuery('');
  };



  const getSelectedEmployees = () => {
    if (multiple && Array.isArray(selectedEmployee)) {
      return employees.filter(emp => selectedEmployee.includes(emp.id));
    }
    return selectedEmployeeData ? [selectedEmployeeData] : [];
  };

  const selectedEmployees = getSelectedEmployees();

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between p-3 border border-gray-300 rounded-lg bg-white hover:border-primary-400 focus:border-primary-600 focus:ring-1 focus:ring-primary-600 transition-all ${
          disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
        } ${isOpen ? 'border-primary-600 ring-1 ring-primary-600' : ''}`}
        disabled={disabled}
      >
        <div className="flex items-center flex-1 min-w-0">
          <Users size={16} className="text-gray-400 mr-2 flex-shrink-0" />

          {selectedEmployees.length > 0 ? (
            <div className="flex items-center flex-1 min-w-0">
              {multiple ? (
                <div className="flex items-center flex-wrap gap-1 flex-1 min-w-0">
                  {selectedEmployees.slice(0, 2).map((employee) => (
                    <span
                      key={employee.id}
                      className="inline-flex items-center px-2 py-1 bg-primary-100 text-primary-700 text-xs rounded-full"
                    >
                      <span className="truncate">{employee.name}</span>
                    </span>
                  ))}
                  {selectedEmployees.length > 2 && (
                    <span className="text-xs text-gray-500">
                      +{selectedEmployees.length - 2} more
                    </span>
                  )}
                </div>
              ) : (
                <div className="flex items-center min-w-0 flex-1">
                  <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center text-xs font-medium text-primary-700 mr-2 flex-shrink-0">
                    {selectedEmployees[0].avatar}
                  </div>
                  <span className="text-sm text-gray-900 truncate">
                    {selectedEmployees[0].name}
                  </span>
                  <span className="text-xs text-gray-500 ml-1 truncate">
                    • {selectedEmployees[0].position}
                  </span>
                </div>
              )}
            </div>
          ) : (
            <span className="text-gray-500 text-sm">{placeholder}</span>
          )}
        </div>

        <div className="flex items-center ml-2">
          {selectedEmployees.length > 0 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
              className="p-1 hover:bg-gray-100 rounded-full mr-1"
            >
              <X size={14} className="text-gray-400" />
            </button>
          )}
          <motion.div
            animate={{ rotate: isOpen ? 180 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <ChevronDown size={16} className="text-gray-400" />
          </motion.div>
        </div>
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="absolute z-[55] mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-elegant max-h-64 overflow-hidden"
            variants={dropdownVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {/* Search Input */}
            <div className="p-3 border-b border-gray-100">
              <input
                ref={inputRef}
                type="text"
                placeholder="Search employees..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-600 focus:border-primary-600"
              />
            </div>

            {/* Employee List */}
            <div className="max-h-48 overflow-y-auto">
              {filteredEmployees.length > 0 ? (
                filteredEmployees.map((employee) => {
                  const isSelected = multiple
                    ? Array.isArray(selectedEmployee) && selectedEmployee.includes(employee.id)
                    : selectedEmployee === employee.id;

                  return (
                    <button
                      key={employee.id}
                      type="button"
                      onClick={() => handleSelect(employee)}
                      className={`w-full flex items-center px-3 py-2 hover:bg-gray-50 transition-colors ${
                        isSelected ? 'bg-primary-50' : ''
                      }`}
                    >
                      <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-sm font-medium text-primary-700 mr-3 flex-shrink-0">
                        {employee.avatar}
                      </div>

                      <div className="flex-1 text-left min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {employee.name}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {employee.position}
                        </p>
                      </div>

                      {isSelected && (
                        <div className="flex-shrink-0">
                          <Check size={16} className="text-primary-600" />
                        </div>
                      )}
                    </button>
                  );
                })
              ) : (
                <div className="px-3 py-4 text-center text-gray-500 text-sm">
                  No employees found
                </div>
              )}
            </div>

            {/* Footer */}
            {selectedEmployees.length > 0 && (
              <div className="border-t border-gray-100 p-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">
                    {selectedEmployees.length} selected
                  </span>
                  <button
                    type="button"
                    onClick={handleClear}
                    className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                  >
                    Clear all
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default EmployeeSelector;
